/**
 * ProjectAnalyticsDashboard — per-project analytics panel.
 *
 * Self-contained: fetches tasks, BOQs, diaries, and files for ONE project.
 * Uses the shared analytics widgets and analytics-engine helpers.
 */

import { useMemo } from "react"
import { useQuery } from "@tanstack/react-query"
import { normalizeBoq, extractArray, type NormalizedBoq } from "@pma/types/xano-models"
import {
  ListTodo,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Banknote,
  Receipt,
  BookOpen,
  FileIcon,
  HardDrive,
} from "lucide-react"
import { Card, CardContent } from "@components/common/card"
import useTranslator from "@hooks/use-translator"
import { cn, formatPercent, getChartColor } from "@pma/utils/helpers"
import { useAuth } from "@pma/stores/auth-store"
import axiosServices from "@pma/utils/axios"
import axiosFinancialServices from "@pma/utils/axiosFinancial"
import {
  TASK_BY_CURRENT_USER_ENDPOINT,
  ALL_MAIN_BOQ_ENDPOINT,
  GET_WORK_DIARY_BY_PROJECT,
  GET_LATEST_FILES,
} from "@pma/utils/api-routes"
import {
  useAnalyticsMetadata,
  resolveEntityStatus,
  buildStatusDonut,
  countByCategory,
  groupAndCount,
} from "./analytics-engine"
import { formatCurrency } from "@pma/utils/utils"
import {
  KpiCard,
  SkeletonKpiCard,
  ChartCard,
  StatusDonutChart,
  AssigneesBarChart,
  CompletionBar,
  DrillDownSheet,
  DrillDownTable,
} from "./widgets"
import type { ColumnDef } from "./widgets"
import { useDrillDown } from "@hooks/use-drill-down"
import { DrillDownState } from "@pma/types"

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ProjectAnalyticsDashboardProps {
  projectId: number
  projectName?: string
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ProjectAnalyticsDashboard({
  projectId,
  projectName,
}: ProjectAnalyticsDashboardProps) {
  const { translate, locale } = useTranslator()
  const isHebrew = locale === "he"
  const { authConfig } = useAuth()
  const orgId = authConfig?.last_organization_id
  const meta = useAnalyticsMetadata()
  const { state: drillDown, open: openDrillDown, close: closeDrillDown } = useDrillDown()

  // ── Stable query options (shared across all queries in this dashboard) ─
  // `staleTime`  — data is fresh for 60 s; no background refetch in that window.
  // `gcTime`     — cache survives 10 min after the last subscriber unmounts,
  //                so switching tabs doesn't discard data we just fetched.
  // `refetchOnMount` — when the component remounts into existing cache, skip
  //                    the network call entirely (cache is still within staleTime).
  const sharedQueryOpts = {
    staleTime: 60_000,
    gcTime: 10 * 60 * 1000,
    refetchOnMount: false as const,
  }

  // ── Tasks ────────────────────────────────────────────────────────────
  const tasksQuery = useQuery({
    queryKey: ["project-analytics-tasks", projectId, orgId],
    queryFn: async () => {
      const res = await axiosServices.post(TASK_BY_CURRENT_USER_ENDPOINT, {
        project_id: projectId,
        organization_id: orgId,
        show_deleted: false,
        paging_per_page: 500,
      })
      return res.data
    },
    enabled: !!projectId && !!orgId,
    // ...sharedQueryOpts,
  })

  const tasks: any[] = useMemo(() => tasksQuery.data?.items || [], [tasksQuery.data])

  // ── BOQs ─────────────────────────────────────────────────────────────
  const boqQuery = useQuery({
    queryKey: ["project-analytics-boq", projectId],
    queryFn: async () => {
      const res = await axiosFinancialServices.post(ALL_MAIN_BOQ_ENDPOINT, {
        project_id: projectId,
        show_deleted: false,
      })
      return res.data
    },
    enabled: !!projectId,
    ...sharedQueryOpts,
  })

  const boqs: NormalizedBoq[] = useMemo(
    () => extractArray(boqQuery.data, normalizeBoq),
    [boqQuery.data],
  )

  // ── Diaries ──────────────────────────────────────────────────────────
  const diaryQuery = useQuery({
    queryKey: ["project-analytics-diaries", projectId, orgId],
    queryFn: async () => {
      const res = await axiosServices.post(GET_WORK_DIARY_BY_PROJECT, {
        project_id: projectId,
        organization_id: orgId,
      })
      return res.data
    },
    enabled: !!projectId && !!orgId,
    ...sharedQueryOpts,
  })

  const diaries: any[] = useMemo(
    () => (Array.isArray(diaryQuery.data) ? diaryQuery.data : diaryQuery.data?.items || []),
    [diaryQuery.data],
  )

  // ── Files ────────────────────────────────────────────────────────────
  const filesQuery = useQuery({
    queryKey: ["project-analytics-files", projectId, orgId],
    queryFn: async () => {
      const res = await axiosServices.get(GET_LATEST_FILES, {
        params: { organization_id: orgId, show_deleted: false },
      })
      return res.data
    },
    enabled: !!projectId && !!orgId,
    ...sharedQueryOpts,
  })

  const files: any[] = useMemo(() => {
    const allFiles = Array.isArray(filesQuery.data) ? filesQuery.data : filesQuery.data?.items || []
    return allFiles.filter((f: any) => f?.project_id === projectId)
  }, [filesQuery.data, projectId])

  // ── Task KPIs ────────────────────────────────────────────────────────
  const taskCounts = useMemo(() => {
    const cats = countByCategory(tasks, meta.statuses)
    const now = Date.now()
    const overdue = tasks.filter((t) => {
      const category = resolveEntityStatus(t, meta.statuses).category
      if (category === "completed" || category === "canceled") return false
      const finish = t.finish_to ? new Date(t.finish_to).getTime() : 0
      return finish > 0 && finish < now
    }).length
    return { ...cats, overdue }
  }, [tasks, meta.statuses])

  // ── Task Status Donut ────────────────────────────────────────────────
  const donutData = useMemo(() => {
    return buildStatusDonut(tasks, meta.statuses).map((d) => ({
      title: d.name,
      count: d.count,
      fill: d.color,
    }))
  }, [tasks, meta.statuses])

  // ── Team Workload ────────────────────────────────────────────────────
  const workloadData = useMemo(() => {
    const grouped = new Map<string, any[]>()
    tasks.forEach((t: any) => {
      const responsibles = t?.responsible_id || []
      if (Array.isArray(responsibles) && responsibles.length > 0) {
        responsibles.forEach((u: any) => {
          const name = typeof u === "object" ? (u?.name || translate("Unassigned")) : translate("Unassigned")
          if (!grouped.has(name)) grouped.set(name, [])
          grouped.get(name)!.push(t)
        })
      } else {
        const label = translate("Unassigned")
        if (!grouped.has(label)) grouped.set(label, [])
        grouped.get(label)!.push(t)
      }
    })
    return Array.from(grouped.entries())
      .map(([name, userTasks], i) => ({
        name,
        count: userTasks.length,
        fill: getChartColor(i),
        tasks: userTasks.slice(0, 5).map((t: any) => ({
          title: t?.title || t?.name || translate("Untitled"),
          status: t?._status?.name || "",
          priority: t?._priority?.name || "",
        })),
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10)
  }, [tasks, translate])

  // ── Financial Summary ────────────────────────────────────────────────
  const financial = useMemo(() => {
    if (!boqs.length) return null
    const totalValue = boqs.reduce((sum, boq) => sum + (boq.realValue || boq.contractValue), 0)
    const totalBilled = boqs.reduce((sum, boq) => sum + boq.paidTotal, 0)
    const remaining = totalValue - totalBilled
    const utilization = totalValue > 0 ? (totalBilled / totalValue) * 100 : 0
    return { totalValue, totalBilled, remaining, utilization }
  }, [boqs])

  // ── Diary Summary ────────────────────────────────────────────────────
  const diarySummary = useMemo(() => {
    const total = diaries.length
    const approved = diaries.filter(
      (d: any) => d.supervisor_approval === true && d.contractor_approval === true,
    ).length
    const approvedPct = total > 0 ? Math.round((approved / total) * 100) : 0

    const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000
    const thisWeek = diaries.filter(
      (d: any) => new Date(d.created_at || d.date).getTime() > weekAgo,
    ).length

    return { total, approved, approvedPct, thisWeek }
  }, [diaries])

  // ── Files Summary ────────────────────────────────────────────────────
  const fileSummary = useMemo(() => {
    const total = files.length
    const totalBytes = files.reduce((sum: number, f: any) => sum + (Number(f.size) || 0), 0)
    const mb = totalBytes / (1024 * 1024)
    const storage = mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${mb.toFixed(1)} MB`
    return { total, storage }
  }, [files])

  // ── Loading ──────────────────────────────────────────────────────────
  const isLoading = tasksQuery.isLoading || meta.isLoading

  if (isLoading) {
    return (
      <div className='grid grid-cols-2 gap-3 md:grid-cols-4'>
        {Array.from({ length: 4 }).map((_, i) => (
          <SkeletonKpiCard key={i} />
        ))}
      </div>
    )
  }

  if (!tasks.length && !boqs.length && !diaries.length) {
    return (
      <Card className='border border-dashed border-gray-200 bg-gray-50/50'>
        <CardContent className='flex flex-col items-center justify-center gap-2 py-10 text-center'>
          <ListTodo className='h-8 w-8 text-muted-foreground/50' />
          <p className='text-sm text-muted-foreground'>
            {translate("create tasks to see analytics")}
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className={cn("flex flex-col gap-4", isHebrew ? "rtl" : "ltr")}>
      {/* Row 1: Task KPIs */}
      <div className='grid grid-cols-2 gap-3 md:grid-cols-4'>
        <KpiCard label={translate("Tasks")} value={taskCounts.total} icon={ListTodo} color='blue' />
        <KpiCard
          label={translate("open tasks")}
          value={taskCounts.open}
          icon={Clock}
          color='amber'
        />
        <KpiCard
          label={translate("completed tasks")}
          value={taskCounts.completed}
          icon={CheckCircle2}
          color='green'
        />
        <KpiCard
          label={translate("Overdue")}
          value={taskCounts.overdue}
          icon={AlertTriangle}
          color='red'
          onClick={
            taskCounts.overdue > 0
              ? () =>
                  openDrillDown({
                    type: "project-tasks",
                    projectId,
                    projectName: projectName || translate("Project"),
                    statusFilter: "overdue",
                  })
              : undefined
          }
        />
      </div>

      {/* Row 2: Donut + Workload */}
      {tasks.length > 0 && (
        <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
          <ChartCard
            title={translate("Task Status")}
            description={translate("click segment to drill down")}
          >
            <StatusDonutChart
              data={donutData}
              total={taskCounts.total}
              translate={translate}
              isHebrew={isHebrew}
              centerLabel={translate("Tasks")}
              onSliceClick={(entry) => {
                openDrillDown({
                  type: "project-tasks",
                  projectId,
                  projectName: entry.title,
                  statusFilter: entry.title,
                })
              }}
            />
          </ChartCard>

          <ChartCard
            title={translate("Team Workload")}
            description={translate("Tasks per team member")}
          >
            {workloadData.length > 0 ? (
              <AssigneesBarChart
                data={workloadData}
                isHebrew={isHebrew}
                translate={translate}
                onBarClick={(entry) => {
                  openDrillDown({ type: "assignee", userName: entry.name })
                }}
              />
            ) : (
              <div className='flex h-[280px] items-center justify-center text-sm text-muted-foreground'>
                {translate("no data available")}
              </div>
            )}
          </ChartCard>
        </div>
      )}

      {/* Row 3: Financial Summary */}
      {financial && (
        <Card className='overflow-hidden border border-gray-100 shadow-sm'>
          <CardContent className='p-4'>
            <div className='mb-3 flex items-center gap-2'>
              <div className='flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-100'>
                <Banknote className='h-4 w-4 text-emerald-600' />
              </div>
              <h3 className='text-sm font-semibold text-foreground'>
                {translate("Financial Summary")}
              </h3>
            </div>
            <div className='grid grid-cols-2 gap-4 md:grid-cols-4'>
              <div
                className='-m-2 cursor-pointer rounded-md p-2 transition-colors hover:bg-gray-50/80'
                onClick={() => openDrillDown({ type: "boq-contract" })}
              >
                <p className='text-xs text-muted-foreground'>{translate("total_boq_value")}</p>
                <p className='font-mono text-lg font-bold text-foreground'>
                  {formatCurrency(financial.totalValue, isHebrew)}
                </p>
              </div>
              <div
                className='-m-2 cursor-pointer rounded-md p-2 transition-colors hover:bg-gray-50/80'
                onClick={() => openDrillDown({ type: "boq-paid" })}
              >
                <p className='text-xs text-muted-foreground'>{translate("total_paid")}</p>
                <p className='font-mono text-lg font-bold text-emerald-600'>
                  {formatCurrency(financial.totalBilled, isHebrew)}
                </p>
              </div>
              <div
                className='-m-2 cursor-pointer rounded-md p-2 transition-colors hover:bg-gray-50/80'
                onClick={() => openDrillDown({ type: "boq-remaining" })}
              >
                <p className='text-xs text-muted-foreground'>{translate("Remaining")}</p>
                <p className='font-mono text-lg font-bold text-amber-600'>
                  {formatCurrency(financial.remaining, isHebrew)}
                </p>
              </div>
              <div
                className='-m-2 cursor-pointer rounded-md p-2 transition-colors hover:bg-gray-50/80'
                onClick={() => openDrillDown({ type: "boq-utilization" })}
              >
                <p className='text-xs text-muted-foreground'>{translate("Utilization")}</p>
                <CompletionBar percent={financial.utilization} />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Row 4: Diary + Files Summary */}
      <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
        {/* Diary */}
        <Card className='overflow-hidden border border-gray-100 shadow-sm'>
          <CardContent className='p-4'>
            <div className='mb-3 flex items-center gap-2'>
              <div className='flex h-7 w-7 items-center justify-center rounded-lg bg-violet-100'>
                <BookOpen className='h-4 w-4 text-violet-600' />
              </div>
              <h3 className='text-sm font-semibold text-foreground'>{translate("Work Diary")}</h3>
            </div>
            <div className='grid grid-cols-3 gap-3 text-center'>
              <div>
                <p className='font-mono text-2xl font-bold text-foreground'>{diarySummary.total}</p>
                <p className='text-xs text-muted-foreground'>{translate("Total")}</p>
              </div>
              <div>
                <p className='font-mono text-2xl font-bold text-green-600'>
                  {diarySummary.approvedPct}%
                </p>
                <p className='text-xs text-muted-foreground'>{translate("Approved")}</p>
              </div>
              <div>
                <p className='font-mono text-2xl font-bold text-blue-600'>
                  {diarySummary.thisWeek}
                </p>
                <p className='text-xs text-muted-foreground'>{translate("This Week")}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Files */}
        <Card className='overflow-hidden border border-gray-100 shadow-sm'>
          <CardContent className='p-4'>
            <div className='mb-3 flex items-center gap-2'>
              <div className='flex h-7 w-7 items-center justify-center rounded-lg bg-sky-100'>
                <FileIcon className='h-4 w-4 text-sky-600' />
              </div>
              <h3 className='text-sm font-semibold text-foreground'>{translate("Files")}</h3>
            </div>
            <div className='grid grid-cols-2 gap-3 text-center'>
              <div>
                <p className='font-mono text-2xl font-bold text-foreground'>{fileSummary.total}</p>
                <p className='text-xs text-muted-foreground'>{translate("Total")}</p>
              </div>
              <div>
                <div className='flex items-center justify-center gap-1'>
                  <HardDrive className='h-4 w-4 text-muted-foreground' />
                  <p className='font-mono text-lg font-bold text-foreground'>
                    {fileSummary.storage}
                  </p>
                </div>
                <p className='text-xs text-muted-foreground'>{translate("Storage")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ── Drill-Down Sheet ─────────────────────────────────────────── */}
      <DrillDownSheet
        open={!!drillDown}
        onClose={closeDrillDown}
        title={getProjectDrillDownTitle(drillDown, translate)}
        subtitle={getProjectDrillDownSubtitle(drillDown, translate)}
        width='wide'
      >
        {drillDown?.type === "project-tasks" &&
          (() => {
            const now = Date.now()
            let filtered = tasks
            if (drillDown.statusFilter === "overdue") {
              filtered = tasks.filter((t) => {
                const cat = resolveEntityStatus(t, meta.statuses).category
                if (cat === "completed" || cat === "canceled") return false
                const finish = t.finish_to ? new Date(t.finish_to).getTime() : 0
                return finish > 0 && finish < now
              })
            } else if (drillDown.statusFilter) {
              filtered = tasks.filter((t) => {
                const display = resolveEntityStatus(t, meta.statuses)
                return display.name === drillDown.statusFilter
              })
            }
            const rows = filtered.map((t: any) => {
              const display = resolveEntityStatus(t, meta.statuses)
              const users = t?.responsible_id || []
              const assignee = Array.isArray(users)
                ? users
                    .map((u: any) => (typeof u === "object" ? u?.name || "Unknown" : `User ${u}`))
                    .join(", ")
                : "-"
              return {
                id: t.id,
                name: t.title || t.name || "-",
                assignee,
                status: display.name,
                statusColor: display.color,
                dueDate: t.finish_to || t.end_date || "-",
              }
            })
            const columns: ColumnDef[] = [
              { header: translate("Task Name"), accessor: "name", sortable: true },
              { header: translate("Assignee"), accessor: "assignee", sortable: true },
              {
                header: translate("status"),
                accessor: "status",
                sortable: true,
                render: (v: string, row: any) => (
                  <span
                    className='inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold'
                    style={{ backgroundColor: row.statusColor + "20", color: row.statusColor }}
                  >
                    {v}
                  </span>
                ),
              },
              { header: translate("Due Date"), accessor: "dueDate", sortable: true },
            ]
            return <DrillDownTable columns={columns} data={rows} />
          })()}
        {(drillDown?.type === "boq-contract" ||
          drillDown?.type === "boq-paid" ||
          drillDown?.type === "boq-remaining" ||
          drillDown?.type === "boq-utilization") &&
          (() => {
            const rows = boqs.map((boq) => ({
              id: boq.id,
              name: boq.name || `BOQ #${boq.id}`,
              contractValue: boq.realValue || boq.contractValue,
              paidTotal: boq.paidTotal,
              remaining: boq.remaining,
              utilization: boq.utilization,
            }))
            // Add totals row
            if (financial) {
              rows.push({
                id: -1,
                name: translate("Total"),
                contractValue: financial.totalValue,
                paidTotal: financial.totalBilled,
                remaining: financial.remaining,
                utilization: financial.utilization,
              })
            }
            const columns: ColumnDef[] = [
              {
                header: translate("BOQ Name"),
                accessor: "name",
                sortable: true,
                render: (v: string, row: any) =>
                  row.id === -1 ? <span className='font-bold'>{v}</span> : v,
              },
              {
                header: translate("Contract") + " (₪)",
                accessor: "contractValue",
                sortable: true,
                render: (v: number, row: any) => (
                  <span className={cn("font-mono text-sm", row.id === -1 && "font-bold")}>
                    {formatCurrency(v, isHebrew)}
                  </span>
                ),
              },
              {
                header: translate("Paid") + " (₪)",
                accessor: "paidTotal",
                sortable: true,
                render: (v: number, row: any) => (
                  <span
                    className={cn(
                      "font-mono text-sm text-emerald-600",
                      row.id === -1 && "font-bold",
                    )}
                  >
                    {formatCurrency(v, isHebrew)}
                  </span>
                ),
              },
              {
                header: translate("Remaining") + " (₪)",
                accessor: "remaining",
                sortable: true,
                render: (v: number, row: any) => (
                  <span
                    className={cn("font-mono text-sm text-amber-600", row.id === -1 && "font-bold")}
                  >
                    {formatCurrency(v, isHebrew)}
                  </span>
                ),
              },
              {
                header: translate("Utilization") + " %",
                accessor: "utilization",
                sortable: true,
                render: (v: number, row: any) => (
                  <div className={cn("flex items-center gap-2", row.id === -1 && "font-bold")}>
                    <div className='h-2 w-16 overflow-hidden rounded-full bg-gray-100'>
                      <div
                        className={cn(
                          "h-full rounded-full transition-all",
                          v >= 90 ? "bg-red-500" : v >= 70 ? "bg-amber-500" : "bg-emerald-500",
                        )}
                        style={{ width: `${Math.min(v, 100)}%` }}
                      />
                    </div>
                    <span className='font-mono text-xs'>{formatPercent(v)}</span>
                  </div>
                ),
              },
            ]
            return <DrillDownTable columns={columns} data={rows} />
          })()}
        {drillDown?.type === "assignee" &&
          (() => {
            const filtered = tasks.filter((t: any) => {
              const users = t?.responsible_id || []
              if (!Array.isArray(users)) return false
              return users.some(
                (u: any) => (typeof u === "object" ? u?.name : `User ${u}`) === drillDown.userName,
              )
            })
            const rows = filtered.map((t: any) => {
              const display = resolveEntityStatus(t, meta.statuses)
              return {
                id: t.id,
                name: t.title || t.name || "-",
                status: display.name,
                statusColor: display.color,
                dueDate: t.finish_to || t.end_date || "-",
              }
            })
            const columns: ColumnDef[] = [
              { header: translate("Task Name"), accessor: "name", sortable: true },
              {
                header: translate("status"),
                accessor: "status",
                sortable: true,
                render: (v: string, row: any) => (
                  <span
                    className='inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold'
                    style={{ backgroundColor: row.statusColor + "20", color: row.statusColor }}
                  >
                    {v}
                  </span>
                ),
              },
              { header: translate("Due Date"), accessor: "dueDate", sortable: true },
            ]
            return <DrillDownTable columns={columns} data={rows} />
          })()}
      </DrillDownSheet>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Project Drill-Down Title / Subtitle helpers
// ---------------------------------------------------------------------------

function getProjectDrillDownTitle(
  drillDown: DrillDownState,
  translate: (key: string) => string,
): string {
  if (!drillDown) return ""
  switch (drillDown.type) {
    case "project-tasks":
      return drillDown.statusFilter === "overdue"
        ? translate("Overdue Tasks")
        : drillDown.projectName
    case "assignee":
      return drillDown.userName
    case "boq-contract":
      return translate("total_boq_value")
    case "boq-paid":
      return translate("total_paid")
    case "boq-remaining":
      return translate("Remaining")
    case "boq-utilization":
      return translate("Utilization")
    default:
      return ""
  }
}

function getProjectDrillDownSubtitle(
  drillDown: DrillDownState,
  translate: (key: string) => string,
): string {
  if (!drillDown) return ""
  switch (drillDown.type) {
    case "project-tasks":
      return drillDown.statusFilter === "overdue"
        ? translate("Tasks past their due date")
        : translate("Tasks with this status")
    case "assignee":
      return translate("Tasks assigned to this person")
    case "boq-contract":
      return translate("Contract value breakdown by BOQ")
    case "boq-paid":
      return translate("Paid amounts breakdown by BOQ")
    case "boq-remaining":
      return translate("Remaining balance breakdown by BOQ")
    case "boq-utilization":
      return translate("Budget utilization breakdown by BOQ")
    default:
      return ""
  }
}

export default ProjectAnalyticsDashboard
